import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { getAllServiceRequests, getServiceStats, updateServiceRequestStatus } from '../../utils/servicesAPI';
import { designSystem, commonStyles } from '../../styles/design-system';

// Styled components
const Container = styled.div`
  ${commonStyles.card}
  margin-bottom: ${designSystem.spacing.xl};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${designSystem.spacing.lg};
  padding-bottom: ${designSystem.spacing.md};
  border-bottom: 2px solid ${designSystem.colors.border.light};
`;

const Title = styled.h2`
  ${commonStyles.titleSecondary}
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.sm};
  margin: 0;

  .icon {
    font-size: ${designSystem.typography.fontSize['2xl']};
  }
`;

const StatsContainer = styled.div`
  display: flex;
  gap: ${designSystem.spacing.md};
  margin-bottom: ${designSystem.spacing.lg};
`;

const StatCard = styled(motion.div)`
  ${commonStyles.card}
  text-align: center;
  padding: ${designSystem.spacing.md};
  min-width: 120px;
  
  .icon {
    font-size: ${designSystem.typography.fontSize['2xl']};
    margin-bottom: ${designSystem.spacing.sm};
  }
  
  .value {
    font-size: ${designSystem.typography.fontSize['3xl']};
    font-weight: ${designSystem.typography.fontWeight.bold};
    ${commonStyles.gradientText}
    margin-bottom: ${designSystem.spacing.xs};
  }
  
  .label {
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.sm};
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: ${designSystem.spacing.md};
  margin-bottom: ${designSystem.spacing.lg};
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  ${commonStyles.input}
  min-width: 150px;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;

  input {
    ${commonStyles.input}
    padding-left: ${designSystem.spacing['3xl']};
  }

  .search-icon {
    position: absolute;
    left: ${designSystem.spacing.md};
    top: 50%;
    transform: translateY(-50%);
    color: ${designSystem.colors.text.secondary};
  }
`;

const RefreshButton = styled.button`
  ${commonStyles.buttonSecondary}
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.xs};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${designSystem.spacing.md};

  th, td {
    padding: ${designSystem.spacing.md};
    text-align: left;
    border-bottom: 1px solid ${designSystem.colors.border.light};
  }

  th {
    background: ${designSystem.colors.background.secondary};
    color: ${designSystem.colors.text.secondary};
    font-weight: ${designSystem.typography.fontWeight.semibold};
    text-transform: uppercase;
    font-size: ${designSystem.typography.fontSize.sm};
    letter-spacing: 1px;
  }

  tr:hover {
    background: rgba(0, 71, 68, 0.05);
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  padding: ${designSystem.spacing.xs} ${designSystem.spacing.sm};
  border-radius: ${designSystem.borderRadius.full};
  font-size: ${designSystem.typography.fontSize.xs};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  
  background: ${props => {
    switch (props.$status) {
      case 'pending': return designSystem.colors.status.warning;
      case 'contacted': return '#3b82f6';
      case 'confirmed': return designSystem.colors.status.success;
      case 'cancelled': return designSystem.colors.status.error;
      case 'completed': return designSystem.colors.primary.main;
      default: return designSystem.colors.secondary.light;
    }
  }};
  
  color: ${props => ['cancelled'].includes(props.$status) ? '#ffffff' : '#000000'};
`;

const ActionGroup = styled.div`
  display: flex;
  gap: ${designSystem.spacing.xs};
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'success' | 'danger' }>`
  padding: ${designSystem.spacing.xs} ${designSystem.spacing.sm};
  border-radius: ${designSystem.borderRadius.md};
  font-size: ${designSystem.typography.fontSize.xs};
  font-weight: ${designSystem.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid;
  
  ${props => {
    switch (props.$variant) {
      case 'success':
        return `
          background: ${designSystem.colors.status.success};
          border-color: ${designSystem.colors.status.success};
          color: #000;
          &:hover { opacity: 0.8; }
        `;
      case 'danger':
        return `
          background: ${designSystem.colors.status.error};
          border-color: ${designSystem.colors.status.error};
          color: #fff;
          &:hover { opacity: 0.8; }
        `;
      case 'secondary':
        return `
          background: transparent;
          border-color: ${designSystem.colors.border.medium};
          color: ${designSystem.colors.text.secondary};
          &:hover { background: ${designSystem.colors.background.secondary}; }
        `;
      default:
        return `
          background: ${designSystem.colors.primary.main};
          border-color: ${designSystem.colors.primary.main};
          color: #fff;
          &:hover { opacity: 0.8; }
        `;
    }
  }}
`;

const LoadingContainer = styled.div`
  ${commonStyles.flexCenter}
  padding: ${designSystem.spacing['5xl']};
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid ${designSystem.colors.border.light};
    border-top: 4px solid ${designSystem.colors.primary.main};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  ${commonStyles.flexCenter}
  flex-direction: column;
  padding: ${designSystem.spacing['4xl']};
  color: ${designSystem.colors.text.secondary};
  
  .icon {
    font-size: ${designSystem.typography.fontSize['5xl']};
    margin-bottom: ${designSystem.spacing.lg};
  }
`;

const RequestDetails = styled.div`
  .service-name {
    font-weight: ${designSystem.typography.fontWeight.semibold};
    color: ${designSystem.colors.primary.main};
  }
  
  .client-name {
    font-size: ${designSystem.typography.fontSize.sm};
    color: ${designSystem.colors.text.secondary};
    margin-top: ${designSystem.spacing.xs};
  }
`;

const ContactInfo = styled.div`
  .email {
    color: ${designSystem.colors.text.primary};
    margin-bottom: ${designSystem.spacing.xs};
  }
  
  .phone {
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.sm};
  }
  
  .whatsapp-link {
    display: inline-flex;
    align-items: center;
    gap: ${designSystem.spacing.xs};
    color: #004744;
    text-decoration: none;
    font-size: ${designSystem.typography.fontSize.sm};
    margin-top: ${designSystem.spacing.xs};
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ServiceRequestsPanel: React.FC = () => {
  const [requests, setRequests] = useState<any[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadRequests();
    loadStats();
  }, [page, statusFilter]);

  useEffect(() => {
    filterRequests();
  }, [requests, searchTerm]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const result = await getAllServiceRequests(statusFilter, page, 20);
      if (result.success) {
        setRequests(result.requests || []);
        setTotal(result.total || 0);
      }
    } catch (error) {
      console.error('Errore caricamento richieste:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await getServiceStats();
      if (result.success) {
        setStats(result.stats || {});
      }
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    }
  };

  const filterRequests = () => {
    if (!searchTerm) {
      setFilteredRequests(requests);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = requests.filter(request =>
      request.client_name?.toLowerCase().includes(searchLower) ||
      request.client_email?.toLowerCase().includes(searchLower) ||
      request.service?.title_it?.toLowerCase().includes(searchLower) ||
      request.provider?.name?.toLowerCase().includes(searchLower)
    );
    
    setFilteredRequests(filtered);
  };

  const updateStatus = async (requestId: string, newStatus: string, notes?: string) => {
    try {
      const result = await updateServiceRequestStatus(requestId, newStatus, notes);
      if (result.success) {
        loadRequests();
        loadStats();
      }
    } catch (error) {
      console.error('Errore aggiornamento status:', error);
    }
  };

  const getStatusLabel = (status: string): string => {
    const labels: { [key: string]: string } = {
      pending: '⏳ In Attesa',
      contacted: '📞 Contattato',
      confirmed: '✅ Confermato',
      cancelled: '❌ Annullato',
      completed: '🎉 Completato'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUrgencyIcon = (createdAt: string): string => {
    const hoursAgo = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60);
    if (hoursAgo > 24) return '🔴'; // Urgente (>24h)
    if (hoursAgo > 12) return '🟡'; // Attenzione (>12h)
    return '🟢'; // Recente (<12h)
  };

  return (
    <Container>
      <Header>
        <Title>
          <span className="icon">📝</span>
          Richieste Servizi ({total})
        </Title>
        <RefreshButton onClick={loadRequests}>
          🔄 Aggiorna
        </RefreshButton>
      </Header>

      {/* Statistiche */}
      <StatsContainer>
        <StatCard whileHover={{ scale: 1.02, y: -2 }}>
          <div className="icon">⏳</div>
          <div className="value">{stats.requestsByStatus?.pending || 0}</div>
          <div className="label">In Attesa</div>
        </StatCard>
        
        <StatCard whileHover={{ scale: 1.02, y: -2 }}>
          <div className="icon">📞</div>
          <div className="value">{stats.requestsByStatus?.contacted || 0}</div>
          <div className="label">Contattati</div>
        </StatCard>
        
        <StatCard whileHover={{ scale: 1.02, y: -2 }}>
          <div className="icon">✅</div>
          <div className="value">{stats.requestsByStatus?.confirmed || 0}</div>
          <div className="label">Confermati</div>
        </StatCard>
        
        <StatCard whileHover={{ scale: 1.02, y: -2 }}>
          <div className="icon">🎉</div>
          <div className="value">{stats.requestsByStatus?.completed || 0}</div>
          <div className="label">Completati</div>
        </StatCard>
      </StatsContainer>

      {/* Filtri */}
      <FiltersContainer>
        <SearchInput>
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Cerca per cliente, servizio o provider..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>
        
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Tutti gli stati</option>
          <option value="pending">In Attesa</option>
          <option value="contacted">Contattati</option>
          <option value="confirmed">Confermati</option>
          <option value="completed">Completati</option>
          <option value="cancelled">Annullati</option>
        </FilterSelect>
      </FiltersContainer>

      {/* Tabella richieste */}
      {loading ? (
        <LoadingContainer>
          <div className="spinner"></div>
          <p>Caricamento richieste...</p>
        </LoadingContainer>
      ) : filteredRequests.length === 0 ? (
        <EmptyState>
          <div className="icon">📝</div>
          <h3>Nessuna richiesta trovata</h3>
          <p>Non ci sono richieste servizi che corrispondono ai filtri selezionati.</p>
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Urgenza</th>
              <th>Servizio</th>
              <th>Cliente</th>
              <th>Contatti</th>
              <th>Data Richiesta</th>
              <th>Dettagli</th>
              <th>Status</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map((request) => (
              <tr key={request.id}>
                <td style={{ textAlign: 'center', fontSize: '1.5rem' }}>
                  {getUrgencyIcon(request.created_at)}
                </td>
                
                <td>
                  <RequestDetails>
                    <div className="service-name">
                      {request.service?.title_it || 'N/A'}
                    </div>
                    <div className="client-name">
                      Provider: {request.provider?.name || 'N/A'}
                    </div>
                  </RequestDetails>
                </td>
                
                <td>
                  <strong>{request.client_name}</strong>
                </td>
                
                <td>
                  <ContactInfo>
                    <div className="email">✉️ {request.client_email}</div>
                    {request.client_phone && (
                      <div className="phone">📞 {request.client_phone}</div>
                    )}
                    {request.provider?.whatsapp && (
                      <a
                        href={`https://wa.me/${request.provider.whatsapp.replace('+', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whatsapp-link"
                      >
                        📱 Provider WhatsApp
                      </a>
                    )}
                  </ContactInfo>
                </td>
                
                <td>
                  <div>{formatDate(request.created_at)}</div>
                  {request.requested_date && (
                    <div style={{ fontSize: '0.85rem', color: designSystem.colors.text.secondary, marginTop: '4px' }}>
                      🗓️ Richiesta: {new Date(request.requested_date).toLocaleDateString('it-IT')}
                    </div>
                  )}
                </td>
                
                <td>
                  <div>👥 {request.participants_count} partecipanti</div>
                  {request.preferred_time_slots?.length > 0 && (
                    <div style={{ fontSize: '0.85rem', color: designSystem.colors.text.secondary, marginTop: '4px' }}>
                      ⏰ {request.preferred_time_slots.join(', ')}
                    </div>
                  )}
                  {request.message && (
                    <div style={{ fontSize: '0.85rem', color: designSystem.colors.text.secondary, marginTop: '4px' }}>
                      💬 {request.message.substring(0, 50)}...
                    </div>
                  )}
                </td>
                
                <td>
                  <StatusBadge $status={request.status}>
                    {getStatusLabel(request.status)}
                  </StatusBadge>
                </td>
                
                <td>
                  <ActionGroup>
                    {request.status === 'pending' && (
                      <>
                        <ActionButton
                          $variant="success"
                          onClick={() => updateStatus(request.id, 'contacted', 'Cliente contattato')}
                          title="Segna come contattato"
                        >
                          📞 Contattato
                        </ActionButton>
                        <ActionButton
                          $variant="primary"
                          onClick={() => updateStatus(request.id, 'confirmed')}
                          title="Conferma servizio"
                        >
                          ✅ Conferma
                        </ActionButton>
                      </>
                    )}
                    
                    {request.status === 'contacted' && (
                      <>
                        <ActionButton
                          $variant="success"
                          onClick={() => updateStatus(request.id, 'confirmed')}
                        >
                          ✅ Conferma
                        </ActionButton>
                        <ActionButton
                          $variant="danger"
                          onClick={() => updateStatus(request.id, 'cancelled')}
                        >
                          ❌ Annulla
                        </ActionButton>
                      </>
                    )}
                    
                    {request.status === 'confirmed' && (
                      <ActionButton
                        $variant="primary"
                        onClick={() => updateStatus(request.id, 'completed')}
                      >
                        🎉 Completato
                      </ActionButton>
                    )}
                    
                    <ActionButton
                      $variant="secondary"
                      onClick={() => window.open(`mailto:${request.client_email}`)}
                      title="Invia email"
                    >
                      ✉️
                    </ActionButton>
                  </ActionGroup>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default ServiceRequestsPanel; 